import {
  Button,
  Checkbox,
  DatePicker,
  MultiSelectInput,
  TextInput,
} from "@pickit/pickit-components";
import useValidationSchema from "hooks/useValidationSchema";
import "pages/pickers/filter/filter.scss";
import "./preliquidationFilter.scss";
import React, { useCallback, useState } from "react";
import { Field, Form } from "react-final-form";
import {
  DATE_FORMATS,
  getFilterPreliquidationOptions,
  VALIDATION_REGEX,
} from "utils/constants";
import {
  PreliquidationFilterPropsType,
  PreliquidationFiltersType,
} from "./types";
import "./i18n";
import { useTranslation } from "react-i18next";
import FilterPanel from "component/filterPanel/FilterPanel";
import { FILTER_PANEL_FORM_CLASS } from "component/filterPanel/types";

export const PreliquidationFilter: React.FC<PreliquidationFilterPropsType> = ({
  onSubmit,
  filters,
  validationSchema,
  fiscalNumberLength,
  testId,
  setCollapseTable,
  countryCode,
}): JSX.Element => {
  const { t } = useTranslation();
  const [disabledField, setDisabledField] = useState("");

  const handleInputChange = useCallback((fieldName: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e !== null) {
        setDisabledField((prev) => {return e ? fieldName : ""});
      }
    },
    []
  );

  return (
    <FilterPanel
      layout="preliquidation"
      title={t("global:filter")}
      testId={testId}
      collapseTable={setCollapseTable}
    >
      <div className="container-row">
          <div className="container-preliquidation-col">
              <Form
                onSubmit={(value: PreliquidationFiltersType) => onSubmit(value)}
                initialValues={filters}
                validate={useValidationSchema(validationSchema)}
              >
                {({ handleSubmit, form, dirty, invalid }) => (
                  <form
                    className={FILTER_PANEL_FORM_CLASS}
                    onSubmit={handleSubmit}
                  >
                    <div className="container-preliquidation-col-sm-3">
                      <Field
                        type="text"
                        name="presettlementId"
                        label={t(
                          "filterPresettlements:preliquidationNumberLabel"
                        )}
                        component={TextInput}
                        className="Admin-Pickers-input test"
                        placeholder={t("filterPresettlements:enterNumber")}
                        maxLength={9}
                        testId={`${testId}-field-presettlementId`}
                      />
                    </div>
                    <div className="container-preliquidation-col-sm-3">
                      <Field
                        type="text"
                        name="fiscalNumber"
                        label={t("filterPresettlements:fiscalNumberLabel")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        placeholder={t(
                          "filterPresettlements:fiscalNumberPlaceholder"
                        )}
                        maxLength={fiscalNumberLength}
                        testId={`${testId}-field-fiscal-number`}
                        pattern={VALIDATION_REGEX.lettersAndNumbers}
                        onChange={handleInputChange("fiscalNumber")}
                        disabled={disabledField === "documentationIncomplete"}
                      />
                    </div>
                    <div className="container-preliquidation-col-sm-3">
                      <div
                        className="datePicker-filter-transaction"
                        id="datePicker-filter-transaction"
                        data-testId={`${testId}-field-generatedAt-label`}
                      >
                        <Field
                          type="text"
                          className="Admin-Pickers-input-select"
                          name="generatedAt"
                          placeholder={t(
                            "filterPresettlements:datePlaceholder"
                          )}
                          maxLength={8}
                          language="es"
                          label={t("filterPresettlements:dateLabel")}
                          outputFormat="DD/MM/YY"
                          testId={`${testId}-field-generatedAt`}
                        >
                          {(props: any) => (
                            <DatePicker
                              singleSelection={true}
                              outputFormat={DATE_FORMATS.shortDate}
                              {...props}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                    <div className="container-preliquidation-col-sm-3">
                      <Field
                        name="status"
                        label={t("filterPresettlements:stateLabel")}
                        placeholder={t("filterPresettlements:statePlaceholder")}
                        options={getFilterPreliquidationOptions(countryCode).map((o) => ({
                          ...o,
                          label: t(o.label),
                        }))}
                        testId={`${testId}-field-status`}
                      >
                        {(props: any) => (
                          <MultiSelectInput showProperty="label" {...props} />
                        )}
                      </Field>
                    </div>
                    <div className="container-preliquidation-col-sm-3">
                      <Field
                        name="pickerId"
                        label={t("filterPresettlements:pickerIdLabel")}
                        placeholder={t(
                          "filterPresettlements:pickerIdPlaceholder"
                        )}
                        testId={`${testId}-field-picker-id`}
                        maxLength={15}
                        component={TextInput}
                        pattern={VALIDATION_REGEX.regNumber}
                      />
                    </div>
                    <div className="container-preliquidation-col-sm-3 checkbox-filter-transaction">
                      <Field
                        name="documentationIncomplete"
                        id="documentationIncomplete"
                        testId={`${testId}-documentation-incomplete`}
                      >
                        {(props: any) => (
                          <Checkbox
                            {...props}
                            label={t(
                              "filterPresettlements:documentationIncomplete"
                            )}
                            onChange={handleInputChange("documentationIncomplete")}
                            disabled={disabledField === "fiscalNumber"}
                          />
                        )}
                      </Field>
                    </div>
                    <div className="container-preliquidation-col-sm-6 end">
                      <Button
                        iconName="search"
                        testId={`${testId}-search-button`}
                        type="submit"
                        label={t("global:search")}
                        hierarchy="secondary"
                        disabled={invalid}
                      />
                    </div>
                  </form>
                )}
              </Form>
          </div>
        </div>
    </FilterPanel>
  );
};
